/**
 * The playout stream client (`@acp/api-contract/ws.js`'s playout section,
 * PLAN §6.6): subscribe to a production over WS, keep its `PlayoutState` by
 * applying `playout.state` frames — a `full` replaces it, a `delta` applies
 * on top of exactly `fromSeq` — and re-read `GET /productions/{id}/playout`
 * whenever a frame shows a gap. Reconnects with the watch client's backoff;
 * every (re)connect subscribes again and so starts from a full state — the
 * one held is dropped with the connection, as a restarted production counts
 * its seq from 0 again.
 *
 * Shared by `acp playout watch` and (later) the SPA's playout view.
 */
import type { DeviceStatus, PlayoutState, PlayoutStateChange } from '@acp/api-contract/playout.js';
import { parseServerMessage, type ServerMessage } from '@acp/api-contract/ws.js';
import type { SocketFactory, SocketLike, Timers } from './ports.js';
import { backoffMs, KEEPALIVE_MS } from './watchClient.js';

/** The state after a change: applied, or a gap (a delta not on top of the state held). */
export type Applied = { readonly state: PlayoutState } | { readonly gap: { readonly have: number | null; readonly fromSeq: number } };

/**
 * Apply one `playout.state` change to the state held (null: none yet). Pure.
 * A delta whose `fromSeq` is not the held `seq` is a gap; a stale full state
 * (older `seq` than held) is ignored.
 */
export function applyPlayoutChange(held: PlayoutState | null, change: PlayoutStateChange): Applied {
  if (change.kind === 'full') return { state: held !== null && change.state.seq < held.seq ? held : change.state };
  if (held === null || held.seq !== change.fromSeq) return { gap: { have: held?.seq ?? null, fromSeq: change.fromSeq } };
  // Each member present in the delta replaces the held one whole (null included: `previous: null` is a change).
  // Every member the delta names, not a list of them: a list here silently dropped `takeCount`, `lastTakeTime`,
  // `startedPlayback`, `autoNext` and `history` (found building the SPA's playout view), and would drop the next
  // member a later schema adds.
  const changed: Partial<PlayoutState> = {};
  for (const [key, value] of Object.entries(change.changes)) if (value !== undefined) Reflect.set(changed, key, value);
  return { state: { ...held, ...changed, seq: change.seq } };
}

export type PlayoutWatchEvent =
  | { readonly type: 'state'; readonly state: PlayoutState; readonly source: 'full' | 'delta' | 'resync' }
  | { readonly type: 'devices'; readonly devices: readonly DeviceStatus[] }
  /** The node lost (`down`) or regained (`up`) its link to the production process: while down, the state held is stale. */
  | { readonly type: 'link'; readonly status: 'up' | 'down'; readonly message: string | null }
  | { readonly type: 'nack'; readonly message: Extract<ServerMessage, { type: 'nack' }> }
  | { readonly type: 'disconnected'; readonly code: number; readonly reason: string; readonly retryInMs: number }
  | { readonly type: 'warning'; readonly message: string };

export type PlayoutWatchOptions = {
  readonly wsUrl: string;
  readonly productionId: string;
  /** The envelope's routing hint (the server acts on the token's tenant, D13). */
  readonly tenantId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  /** `GET /productions/{id}/playout`: the resync after a gap. */
  readonly fetchState: () => Promise<PlayoutState>;
  readonly onEvent: (event: PlayoutWatchEvent) => void;
};

export type PlayoutWatchHandle = { readonly stop: () => void; readonly done: Promise<void> };

export function watchPlayout(opts: PlayoutWatchOptions): PlayoutWatchHandle {
  let stopped = false;
  let socket: SocketLike | null = null;
  let attempt = 0;
  let held: PlayoutState | null = null;
  let cancelKeepalive: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  const { promise: done, resolve: resolveDone, reject: rejectDone } = Promise.withResolvers<void>();

  const resync = async (): Promise<void> => {
    try {
      const state = await opts.fetchState();
      if (held === null || state.seq >= held.seq) {
        held = state;
        opts.onEvent({ type: 'state', state, source: 'resync' });
      }
    } catch (err) {
      opts.onEvent({ type: 'warning', message: `resync failed: ${err instanceof Error ? err.message : String(err)}` });
    }
  };

  /** A frame other than a playout state: a nack, this production's devices or link, or nothing to report. */
  const onOther = (message: ServerMessage): void => {
    if (message.type === 'nack') opts.onEvent({ type: 'nack', message });
    else if (message.type === 'device.state' && message.productionId === opts.productionId) opts.onEvent({ type: 'devices', devices: message.payload.devices });
    else if (message.type === 'playout.link' && message.productionId === opts.productionId) {
      // Down: the process behind the stream is gone. Whatever comes back may count its seq afresh (a relaunch that
      // did not restore a snapshot), so, as on a closed socket, nothing held may judge its full state as stale.
      if (message.payload.status === 'down') held = null;
      opts.onEvent({ type: 'link', ...message.payload });
    }
  };

  const onMessage = (data: unknown): void => {
    const parsed = parseServerMessage(typeof data === 'string' ? data : String(data));
    if (!parsed.ok) {
      opts.onEvent({ type: 'warning', message: `unparseable frame: ${parsed.error}` });
      return;
    }
    const message = parsed.message;
    if (message.type !== 'playout.state') {
      onOther(message);
      return;
    }
    if (message.productionId !== opts.productionId) return;
    attempt = 0;
    const applied = applyPlayoutChange(held, message.payload);
    if ('gap' in applied) {
      opts.onEvent({ type: 'warning', message: `missed a change (have seq ${String(applied.gap.have)}, delta from ${String(applied.gap.fromSeq)}): re-reading the state` });
      void resync();
      return;
    }
    if (applied.state === held) return;
    held = applied.state;
    opts.onEvent({ type: 'state', state: applied.state, source: message.payload.kind });
  };

  const connect = async (): Promise<void> => {
    let token: string;
    try {
      token = await opts.token();
    } catch (err) {
      stopped = true;
      rejectDone(err);
      return;
    }
    if (stopped) return;
    const s = opts.openSocket(`${opts.wsUrl}?token=${encodeURIComponent(token)}`);
    socket = s;
    s.onopen = () => {
      s.send(JSON.stringify({ type: 'playout.subscribe', tenantId: opts.tenantId, productionId: opts.productionId, payload: {}, ts: opts.nowMs() }));
      cancelKeepalive = opts.timers.every(KEEPALIVE_MS, () => s.send(JSON.stringify({ type: 'ping', tenantId: opts.tenantId, payload: {}, ts: opts.nowMs() })));
    };
    s.onmessage = (event) => {
      onMessage(event.data);
    };
    s.onerror = () => undefined;
    s.onclose = (event) => {
      cancelKeepalive?.();
      cancelKeepalive = null;
      socket = null;
      // The next connection subscribes afresh and gets a full state; the production may have restarted
      // (its seq from 0), so nothing held from this connection may judge what comes next as stale.
      held = null;
      if (stopped) return;
      const retryInMs = backoffMs(attempt);
      attempt += 1;
      opts.onEvent({ type: 'disconnected', code: event.code, reason: event.reason, retryInMs });
      cancelRetry = opts.timers.after(retryInMs, () => {
        cancelRetry = null;
        void connect();
      });
    };
  };

  void connect();

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      cancelRetry?.();
      cancelKeepalive?.();
      socket?.close();
      resolveDone();
    },
    done,
  };
}
